<template>
  <div class="map-location-panel">
    <!-- 内嵌地图容器（v2 瓦片）：未就绪时占位（加载中/未配置Key/失败重试） -->
    <div v-show="mapReady" ref="mapBox" class="map-box"></div>
    <div v-if="!mapReady" class="map-box map-placeholder">
      <a-spin v-if="mapLoading" />
      <a-empty
        v-else-if="!configured"
        description="未配置腾讯地图Key，请到 系统管理→系统配置→地址定位与地图服务 维护后使用" />
      <div v-else-if="loadFailed" class="map-fail">
        <a-empty>
          <template slot="description">
            <div class="map-fail-text">地图加载失败：请到腾讯位置服务控制台(lbs.qq.com)检查该 Key 已启用「Web端(JS API)」产品，且域名白名单包含 erp.mgskerp.cn</div>
          </template>
        </a-empty>
        <a-button type="primary" @click="retryLoad">重试</a-button>
      </div>
      <a-empty v-else description="地图加载中，请稍候" />
    </div>
    <!-- 经纬度行：搜索/点选/拖拽自动回填；手改仅地图跟随并 emit（不触发逆解析） -->
    <a-row class="panel-row">
      <span class="coord-label">纬度</span>
      <a-input-number v-model="localLat" :min="-90" :max="90" :precision="6" :disabled="disabled" style="width: 160px" @change="onManualLatLng" />
      <span class="coord-label coord-gap">经度</span>
      <a-input-number v-model="localLng" :min="-180" :max="180" :precision="6" :disabled="disabled" style="width: 160px" @change="onManualLatLng" />
    </a-row>
  </div>
</template>

<script>
import { mapKey, mapSearch, mapGeocoder } from '@/api/plugin/dining'

/**
 * 内嵌地图定位面板（完全照云贝 selectmap：腾讯 JS API v2 版本及逻辑，默认红标 Marker 可拖拽）：
 *   1. 加载：两段注入——v2.exp loader（内部 document.write 在 SPA 动态注入下被忽略）
 *      → 手动补注 main.js（loader 内 getScript 同款地址）→ 轮询 qq.maps.Map 就绪
 *      （云贝经 PHP 输出 document.write 在解析期引入，本 SPA 场景等价实现）
 *   2. 地址搜索：后端 /map/search 代理定位打标（关键词由父组件拼好——含省市区前缀）
 *   3. 地图点选/Marker 拖拽：qq.maps.event 取经纬度 + /map/geocoder 逆解析回填地址（云贝 channel/shop/map 同款）
 *   4. change 事件统一向父组件同步 {lat, lng, address}
 */
const QQMAP_V2_FLAG = '__JSH_QQMAP_V2__'
// v2 main.js 固定地址（来源：loader 源码 getScript("https://mapapi.qq.com/jsapi_v2/2/4/160/main.js")；
// 腾讯升级版本号时需同步更新此处）
const V2_MAIN_JS = 'https://mapapi.qq.com/jsapi_v2/2/4/160/main.js'
const LOAD_TIMEOUT = 12000
// 经纬度缺省回落武汉（云贝同款默认）
const DEFAULT_LAT = 30.59331
const DEFAULT_LNG = 114.304692

export default {
  name: 'MapLocationPanel',
  props: {
    // 初始纬度（Number/String 均可，缺省回落武汉）
    lat: { type: [Number, String], default: '' },
    // 初始经度
    lng: { type: [Number, String], default: '' },
    // 初始地址
    address: { type: String, default: '' },
    // 只读模式（decentralize-store-pay D：关收款门店信息由总店维护，禁点选/拖拽/搜索/手改）
    disabled: { type: Boolean, default: false }
  },
  data() {
    return {
      addressInput: '',
      localLat: DEFAULT_LAT,
      localLng: DEFAULT_LNG,
      configured: false,
      mapLoading: false,
      loadFailed: false,
      mapReady: false,
      searching: false,
      hasInitPick: false,
      isDestroyed: false,
      map: null,
      marker: null,
      manualTimer: null
    }
  },
  computed: {
    // 父组件经纬度键（无效为空串）：watch 据此同步地图，与本地一致时（自身 emit 回声）跳过
    propKey() {
      const lat = this.toNum(this.lat)
      const lng = this.toNum(this.lng)
      return (lat === null || lng === null) ? '' : lat + ',' + lng
    }
  },
  watch: {
    // 地址 props 变化同步内部值（父表单异步加载场景；自身 emit 回声时值相同自动跳过）
    address(val) {
      if (val !== this.addressInput) {
        this.addressInput = val || ''
      }
    },
    // 经纬度 props 变化同步地图（编辑态异步加载的存量定位）；与本地一致（自身 emit 回声）时跳过防死循环
    propKey(key) {
      if (!key) {
        return
      }
      const parts = key.split(',')
      const lat = Number(parts[0])
      const lng = Number(parts[1])
      if (lat === this.localLat && lng === this.localLng) {
        return
      }
      this.localLat = lat
      this.localLng = lng
      this.hasInitPick = true
      if (this.map) {
        this.map.setCenter(new window.qq.maps.LatLng(lat, lng))
        this.moveMarker(lat, lng)
      }
    }
  },
  mounted() {
    // 初始状态：地址取 props，经纬度有效则定位打标，缺省回落武汉（云贝同款）
    this.addressInput = this.address || ''
    const lat = this.toNum(this.lat)
    const lng = this.toNum(this.lng)
    this.hasInitPick = lat !== null && lng !== null
    this.localLat = this.hasInitPick ? lat : DEFAULT_LAT
    this.localLng = this.hasInitPick ? lng : DEFAULT_LNG
    this.loadMap()
  },
  beforeDestroy() {
    // 销毁清理（v2 无 destroy；DOM 移除后地图实例随之失效，置 null 即可）
    this.isDestroyed = true
    if (this.manualTimer) {
      clearTimeout(this.manualTimer)
      this.manualTimer = null
    }
    this.map = null
    this.marker = null
  },
  methods: {
    // 数值安全转换：空/非法返回 null（避免 Number('')===0 误判为有效经纬度）
    toNum(v) {
      if (v === '' || v === null || v === undefined) {
        return null
      }
      const n = Number(v)
      return isNaN(n) ? null : n
    },
    // 拉取租户地图 Key 并加载 v2 SDK（未配置显示引导；异常/超时进入失败态）
    loadMap() {
      this.mapLoading = true
      this.loadFailed = false
      mapKey().then(res => {
        if (this.isDestroyed) {
          return
        }
        if (res.code === 200 && res.data) {
          this.configured = !!res.data.configured
          if (!this.configured) {
            this.mapLoading = false
            return
          }
          return this.loadV2Script(res.data.key).then(() => {
            this.initMap()
          })
        }
        this.loadFailed = true
        this.mapLoading = false
      }).catch(() => {
        if (this.isDestroyed) {
          return
        }
        this.loadFailed = true
        this.mapLoading = false
      })
    },
    // 失败态重试：重置加载状态重新走加载流程
    retryLoad() {
      this.loadMap()
    },
    /**
     * v2 两段注入（云贝解析期 document.write 的 SPA 等价实现）：
     *   ① 注入 loader（api/js?v=2.exp&key=）——其内部 document.write 加载 main.js 会被浏览器忽略
     *   ② onload 后 qq.maps.Map 仍无 → 手动补注 main.js（loader 源码内 getScript 的同款固定地址）
     *   ③ 轮询 qq.maps.Map 为 function（200ms 间隔，12s 超时）
     */
    loadV2Script(key) {
      if (window.qq && window.qq.maps && typeof window.qq.maps.Map === 'function') {
        return Promise.resolve()
      }
      if (!window[QQMAP_V2_FLAG]) {
        window[QQMAP_V2_FLAG] = new Promise((resolve, reject) => {
          const inject = (src, onload, onerror) => {
            const s = document.createElement('script')
            s.src = src
            s.onload = onload
            s.onerror = onerror
            document.head.appendChild(s)
          }
          const fail = () => {
            window[QQMAP_V2_FLAG] = null
            reject(new Error('qqmap v2 load error'))
          }
          // ① loader（定义 qq.maps.__load，document.write 注 main.js 在动态注入下无效）
          inject('https://map.qq.com/api/js?v=2.exp&key=' + encodeURIComponent(key || ''), () => {
            if (window.qq && window.qq.maps && typeof window.qq.maps.Map === 'function') {
              resolve()
              return
            }
            // ② 补注 main.js（云贝链路的解析期步骤，手动等价执行）
            inject(V2_MAIN_JS, () => {
              // ③ 轮询等 qq.maps.Map 就绪（main.js 异步加载 mods 后才定义）
              const start = Date.now()
              const timer = setInterval(() => {
                if (window.qq && window.qq.maps && typeof window.qq.maps.Map === 'function') {
                  clearInterval(timer)
                  resolve()
                } else if (Date.now() - start > LOAD_TIMEOUT) {
                  clearInterval(timer)
                  fail()
                }
              }, 200)
            }, fail)
          }, fail)
        })
      }
      return window[QQMAP_V2_FLAG]
    },
    // SDK 就绪后延迟初始化（云贝节奏：nextTick+500ms 等容器渲染出尺寸再建图）
    initMap() {
      if (this.isDestroyed || this.map || !this.$refs.mapBox) {
        return
      }
      this.mapReady = true
      this.$nextTick(() => {
        setTimeout(() => {
          if (this.isDestroyed || this.map || !this.$refs.mapBox) {
            return
          }
          // 全段防御：标注等任何异常不连坐事件绑定
          try {
            const maps = window.qq.maps
            // 云贝同款：Map + 默认中心（zoom 15 略近，便于定位后直接看街区）
            this.map = new maps.Map(this.$refs.mapBox, {
              center: new maps.LatLng(this.localLat, this.localLng),
              zoom: 15
            })
            // 缩放控件（Edge 高 DPI 下滚轮缩放异常，控件手动可缩到街道级 19；异常不连坐主流程）
            try {
              new maps.NavigationControl({ map: this.map })
            } catch (ctrlErr) {
              console.error('缩放控件创建失败', ctrlErr)
            }
            // 地图点选取经纬度（云贝同款：qq.maps.event + e.latLng.getLat/getLng）
            maps.event.addListener(this.map, 'click', e => {
              if (!e || !e.latLng) {
                return
              }
              this.handlePick(e.latLng.getLat(), e.latLng.getLng())
            })
            // 打开页面即建可拖拽默认红标（云贝同款 Marker：无初值落默认中心）
            this.moveMarker(this.localLat, this.localLng)
          } catch (e) {
            console.error('地图初始化异常', e)
          } finally {
            this.mapLoading = false
          }
        }, 500)
      })
    },
    // 创建/移动标注（云贝同款：默认红标 Marker + draggable，dragend 回填）
    moveMarker(lat, lng) {
      if (!this.map) {
        return
      }
      const maps = window.qq.maps
      const position = new maps.LatLng(lat, lng)
      if (!this.marker) {
        this.marker = new maps.Marker({ map: this.map, position: position, draggable: true })
        // 标注拖拽结束：取标注当前位置，走同一点选同步逻辑（云贝同款）
        maps.event.addListener(this.marker, 'dragend', e => {
          if (!e || !e.latLng) {
            return
          }
          this.handlePick(e.latLng.getLat(), e.latLng.getLng())
        })
      } else {
        this.marker.setPosition(position)
      }
    },
    // 点选/拖拽统一同步（两段式）：立即回填经纬度+标注+居中并先 emit（经纬度先落表单），
    // 再逆解析详细地址成功后二次 emit 回填表单地址；逆解析失败静默（经纬度已回填不阻塞）
    handlePick(lat, lng) {
      if (this.isDestroyed || this.disabled) {
        return
      }
      this.localLat = lat
      this.localLng = lng
      try {
        if (this.map) {
          this.map.setCenter(new window.qq.maps.LatLng(lat, lng))
        }
        this.moveMarker(lat, lng)
      } catch (e) {
        console.error('地图定位操作异常', e)
      }
      this.hasInitPick = true
      this.$emit('change', { lat: lat, lng: lng, address: this.addressInput })
      mapGeocoder({ lat: lat, lng: lng }).then(res => {
        if (res.code === 200 && res.data && res.data.address) {
          this.addressInput = res.data.address
          this.$emit('change', { lat: lat, lng: lng, address: res.data.address })
        }
      }).catch(() => { /* 逆解析失败静默，经纬度已同步 */ })
    },
    /**
     * 地址搜索（父组件地址栏按钮/回车调用，关键词由父拼好——含所属区域省市区前缀提升 POI 精度）：
     * 成功 → 回填经纬度框+地图居中+标注落点+成功提示（地图操作 try/catch 隔离，异常不吞成功状态）+ emit
     */
    doSearch(keyword, originAddress) {
      if (this.disabled) {
        return
      }
      const kw = (keyword || '').trim()
      if (!kw) {
        this.$message.warning('请输入地址')
        return
      }
      if (!this.configured) {
        this.$message.warning('未配置腾讯地图Key，请先到系统配置维护')
        return
      }
      if (!this.map) {
        this.$message.warning('地图正在加载，请稍候再搜索')
        return
      }
      this.searching = true
      mapSearch({ address: kw }).then(res => {
        if (res.code === 200 && res.data && res.data.location) {
          const loc = res.data.location
          const lat = Number(loc.lat)
          const lng = Number(loc.lng)
          this.localLat = lat
          this.localLng = lng
          this.hasInitPick = true
          // 地图操作异常不再吞掉搜索成功（此前 setCenter 抛错落入 catch 误报"搜索失败"）
          try {
            this.map.setCenter(new window.qq.maps.LatLng(lat, lng))
            this.moveMarker(lat, lng)
          } catch (e) {
            console.error('地图定位操作异常', e)
          }
          this.$message.success('已定位：' + (res.data.title || originAddress || kw))
          this.$emit('change', { lat: lat, lng: lng, address: (originAddress || kw).trim() })
        } else {
          this.$message.warning((res && res.message) || '未搜索到该地址，可尝试在地图上直接点选')
        }
      }).catch(() => {
        this.$message.warning('地址搜索失败，可尝试在地图上直接点选')
      }).finally(() => {
        this.searching = false
      })
    },
    // 经纬度手输（防抖 300ms 合并连续键击）：仅地图跟随 + emit，不触发逆解析
    onManualLatLng() {
      if (this.manualTimer) {
        clearTimeout(this.manualTimer)
      }
      this.manualTimer = setTimeout(() => {
        this.manualTimer = null
        if (this.isDestroyed) {
          return
        }
        const lat = this.toNum(this.localLat)
        const lng = this.toNum(this.localLng)
        if (lat === null || lng === null) {
          return
        }
        if (this.map) {
          this.map.setCenter(new window.qq.maps.LatLng(lat, lng))
          this.moveMarker(lat, lng)
        }
        this.$emit('change', { lat: lat, lng: lng, address: this.addressInput })
      }, 300)
    }
  }
}
</script>

<style scoped>
.panel-row {
  display: flex;
  align-items: center;
  margin: 8px 0;
}
.coord-label {
  color: #666;
  margin-right: 8px;
}
.coord-gap {
  margin-left: 16px;
}
.map-box {
  width: 100%;
  height: 360px;
  border: 1px solid #e8e8e8;
  border-radius: 4px;
  margin: 8px 0;
  box-sizing: border-box;
}
.map-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
}
.map-fail {
  text-align: center;
  width: 100%;
}
.map-fail-text {
  color: #f5222d;
  font-size: 13px;
  line-height: 20px;
  padding: 0 16px;
}
</style>
